import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, GraduationCap, CheckCircle2 } from "lucide-react";
import { GRADES, ORIENTATIONS, SUBJECT_GROUPS, SUBJECT_GROUPS_B, GYMNASIO_SUBJECTS, A_LYKEIOU_SUBJECTS } from "@/data";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  landline: "",
  grade: "",
  orientation: "",
  subjects: [],
  full_package: false,
  notes: "",
};

const inputCls =
  "w-full bg-white border border-border rounded-sm px-4 py-3 text-sm outline-none focus:border-primary transition-colors";
const labelCls = "font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground";

export default function Enrollment() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSubject = (s) =>
    setForm((f) => ({
      ...f,
      subjects: f.subjects.includes(s)
        ? f.subjects.filter((x) => x !== s)
        : [...f.subjects, s],
    }));

  const isLykeioBG = ["Β' Λυκείου", "Γ' Λυκείου"].includes(form.grade);
  const orientationGroups = form.grade === "Β' Λυκείου" ? SUBJECT_GROUPS_B : SUBJECT_GROUPS;
  const isGymnasio = form.grade.includes("Γυμνασίου");
  const isALykeiou = form.grade === "Α' Λυκείου";
  const flatSubjects = isGymnasio ? GYMNASIO_SUBJECTS : isALykeiou ? A_LYKEIOU_SUBJECTS : [];

  const renderFlat = (list) => (
    <div className="mt-4 grid sm:grid-cols-2 gap-3">
      {list.map((s) => (
        <label
          key={s}
          data-testid={`enroll-subject-${s}`}
          className="flex items-center gap-3 border border-border rounded-sm px-4 py-2.5 cursor-pointer hover:border-primary transition-colors"
        >
          <Checkbox checked={form.subjects.includes(s)} onCheckedChange={() => toggleSubject(s)} />
          <span className="text-sm">{s}</span>
        </label>
      ))}
    </div>
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.mobile || !form.grade) {
      toast.error("Συμπληρώστε όνομα, επώνυμο, email, κινητό και τάξη.");
      return;
    }
    if (!form.full_package && form.subjects.length === 0) {
      toast.error("Επιλέξτε μαθήματα ή το πλήρες πακέτο.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enroll`, form);
      setDone(true);
      toast.success("Η αίτηση εγγραφής στάλθηκε! Θα επικοινωνήσουμε σύντομα.");
      setForm(initial);
    } catch (err) {
      toast.error("Κάτι πήγε στραβά. Δοκιμάστε ξανά.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enroll" data-testid="enroll-section" className="py-20 md:py-32">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-4">// Φόρμα Εγγραφής</div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
            Εγγραφή
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Συμπληρώστε τα στοιχεία του μαθητή και επιλέξτε μαθήματα ή το πλήρες πακέτο. Θα επικοινωνήσουμε
            μαζί σας για να ολοκληρώσουμε την εγγραφή.
          </p>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="enroll-success"
            className="bg-white border border-border rounded-sm p-10 text-center"
          >
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" strokeWidth={1.5} />
            <h3 className="font-heading font-bold text-xl mt-5">Λάβαμε την αίτησή σας!</h3>
            <p className="text-muted-foreground mt-2">Θα επικοινωνήσουμε μαζί σας το συντομότερο δυνατό.</p>
            <button
              data-testid="enroll-new"
              onClick={() => setDone(false)}
              className="mt-6 font-mono-accent text-xs uppercase tracking-widest text-primary hover:text-accent transition-colors"
            >
              Νέα αίτηση →
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            onSubmit={onSubmit}
            data-testid="enroll-form"
            className="bg-white border border-border rounded-sm p-6 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>Όνομα *</label>
                <input data-testid="enroll-first-name" value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Όνομα μαθητή" />
              </div>
              <div>
                <label className={labelCls}>Επώνυμο *</label>
                <input data-testid="enroll-last-name" value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Επώνυμο μαθητή" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input data-testid="enroll-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="email@example.com" />
              </div>
              <div>
                <label className={labelCls}>Κινητό τηλέφωνο *</label>
                <input data-testid="enroll-mobile" value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="69XXXXXXXX" />
              </div>
              <div>
                <label className={labelCls}>Σταθερό τηλέφωνο</label>
                <input data-testid="enroll-landline" value={form.landline} onChange={(e) => setField("landline", e.target.value)} className={`mt-2 ${inputCls}`} placeholder="21XXXXXXXX" />
              </div>
              <div>
                <label className={labelCls}>Τάξη *</label>
                <Select value={form.grade} onValueChange={(v) => setForm((f) => ({ ...f, grade: v, subjects: [] }))}>
                  <SelectTrigger data-testid="enroll-grade" className="mt-2 rounded-sm border-border h-[46px]">
                    <SelectValue placeholder="Επιλέξτε τάξη" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADES.map((g) => (
                      <SelectItem key={g} value={g} data-testid={`enroll-grade-${g}`}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Προσανατολισμός</label>
                <Select value={form.orientation} onValueChange={(v) => setField("orientation", v)}>
                  <SelectTrigger data-testid="enroll-orientation" className="mt-2 rounded-sm border-border h-[46px]">
                    <SelectValue placeholder="Επιλέξτε προσανατολισμό (προαιρετικό)" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORIENTATIONS.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`enroll-orientation-${o}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-8 border-t border-border pt-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <label className={labelCls}>Μαθήματα</label>
                <div className="flex items-center gap-3">
                  <span className="font-mono-accent text-[10px] uppercase tracking-widest text-primary">Πλήρες πακέτο</span>
                  <Switch
                    data-testid="enroll-full-package"
                    checked={form.full_package}
                    onCheckedChange={(v) => setField("full_package", v)}
                  />
                </div>
              </div>

              {form.full_package ? (
                <div data-testid="enroll-package-note" className="mt-4 bg-primary/5 border border-primary/20 rounded-sm p-4 flex items-center gap-3">
                  <GraduationCap className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <span className="text-sm">Επιλέχθηκε το <strong>Πλήρες Πακέτο</strong> (όλα τα μαθήματα προσανατολισμού).</span>
                </div>
              ) : !form.grade ? (
                <div data-testid="enroll-grade-hint" className="mt-4 bg-secondary border border-border rounded-sm p-4 text-sm text-muted-foreground">
                  Επιλέξτε πρώτα <strong className="text-foreground">τάξη</strong> για να εμφανιστούν τα διαθέσιμα μαθήματα.
                </div>
              ) : isLykeioBG ? (
                <div className="mt-4 space-y-6">
                  {orientationGroups.map((group) => (
                    <div key={group.id} data-testid={`enroll-group-${group.id}`} className="border border-border rounded-sm overflow-hidden">
                      <div className="bg-primary text-primary-foreground px-4 py-2.5 font-mono-accent text-[11px] uppercase tracking-widest">
                        {group.title}
                      </div>
                      <div className="p-4 space-y-5">
                        {group.tracks.map((track) => (
                          <div key={track.name}>
                            <div className="font-mono-accent text-[10px] uppercase tracking-widest text-accent mb-3">
                              // {track.name}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                              {track.subjects.map((s) => {
                                const value = `${group.title} — ${track.name}: ${s}`;
                                return (
                                  <label
                                    key={value}
                                    data-testid={`enroll-subject-${value}`}
                                    className="flex items-center gap-3 border border-border rounded-sm px-4 py-2.5 cursor-pointer hover:border-primary transition-colors"
                                  >
                                    <Checkbox
                                      checked={form.subjects.includes(value)}
                                      onCheckedChange={() => toggleSubject(value)}
                                    />
                                    <span className="text-sm">{s}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                renderFlat(flatSubjects)
              )}
            </div>

            <div className="mt-6">
              <label className={labelCls}>Σημειώσεις</label>
              <textarea data-testid="enroll-notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="Οτιδήποτε άλλο θέλετε να μας πείτε (προαιρετικό)" />
            </div>

            <button
              data-testid="enroll-submit-button"
              type="submit"
              disabled={loading}
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-mono-accent text-sm uppercase tracking-widest px-8 py-4 rounded-sm hover:bg-accent transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Αποστολή..." : "Υποβολή αίτησης εγγραφής"}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
}
